import React, { FC, memo } from 'react';

import {
  BookingState,
  ICalendarEventType,
  IPharmacistDetails,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  ProfessionalReplacementTypes,
} from '@pharmaplan/common';
import AvatarFallback from '@pharmaplan/common/assets/icons/avatarPlaceholder.png';

import useSelfService from '../../../hooks/useSelfService';
import strings from '../../../localization';
import { ScreenTypes } from '../../../helpers/Constants';

import ServiceCard from '../ServiceCard/ServiceCard';
import PharmacistCardFooter from './PharmacistCardFooter/PharmacistCardFooter';
import PharmacistCardContent from './PharmacistCardContent/PharmacistCardContent';
import styles from './styles';

interface IPharmacistCard {
  pharmacistDetails: Partial<IPharmacistDetails>;
  screenType: ScreenTypes;
  workshiftId?: string;
  date?: string;
  eventIds?: Array<string>;
  eventId?: string;
  type?: ICalendarEventType;
  successAction: string;
  imageSuccessAction?: string;
  goBack?: () => void;
  showRating?: boolean;
  isExternalPharmacist?: boolean;
  customFooterEnd?: React.ReactNode | null;
}

const { None } = BookingState;
const { requested } = PharmacyLegendVariant;
const { booked } = PharmacistLegendVariant;
const { workshiftSummary, pharmacistProfile, replacement } = ScreenTypes;

const PharmacistCard: FC<IPharmacistCard> = ({
  eventId,
  pharmacistDetails,
  type,
  date,
  eventIds,
  workshiftId,
  screenType,
  successAction,
  imageSuccessAction,
  goBack,
  showRating,
  isExternalPharmacist,
  customFooterEnd,
}) => {
  const {
    firstName = '',
    lastName = '',
    language,
    requestedDate,
    professionalCapacity,
    postalCode,
    pharmacistCapacity,
    city,
    profilePicture,
    rating,
    pharmacistId = '',
    professionalId = '',
    isFavorite = false,
    bookingState,
    bookingStateExpiration,
    timezone,
    mobile,
    reviewDetails,
    licenseNumber,
    graduationYear,
    replacementType,
  } = pharmacistDetails ?? {};

  const { totalRating } = rating ?? {};
  const { isSelfService } = useSelfService();
  const capacity = isSelfService ? professionalCapacity : pharmacistCapacity;

  const isBooked = type === booked;
  const isSummary = screenType === workshiftSummary;
  const isPharmacistProfile = screenType === pharmacistProfile;
  const isPharmacist = replacementType === ProfessionalReplacementTypes.pharmacist;
  const isReplacement = screenType === replacement;

  const isRequested = type === requested;
  const isBookingStateNone = bookingState === None;

  const calcRating = ((totalRating ?? 0) * 5) / 100;

  const cardContent = {
    eventId,
    firstName,
    lastName,
    avatar: profilePicture ?? AvatarFallback,
    requestedDate: requestedDate ?? '',
    rating: calcRating,
    pharmacistId: isSelfService ? professionalId : pharmacistId,
    type,
    isFavorite,
    bookingState,
    bookingStateExpiration,
    isSummary,
    timezone,
    workshiftId,
    date,
    eventIds,
    reviewDetails,
  };

  const { from, to } = capacity ?? {};

  const pharmacistFooterConfig = [
    {
      key: '0',
      primaryTitle: strings.preferredLanguage,
      primaryValue: language?.name,
      secondaryTitle: strings.pharmacistCapacity,
      secondaryValue: `${from} - ${to}`,
    },
    {
      key: '1',
      primaryTitle: strings.town,
      primaryValue: city,
      secondaryTitle: strings.postalCode,
      secondaryValue: postalCode,
    },
    isBooked || isReplacement
      ? {
        key: '2',
        primaryTitle: strings.mobile,
        primaryValue: mobile,
      }
      : {},
  ];

  const selfFooterConfig = [
    isPharmacist
      ? {
        key: '0',
        primaryTitle: strings.licenseNo,
        primaryValue: `${licenseNumber} (${graduationYear ?? ''})`,
        secondaryTitle: strings.town,
        secondaryValue: city,
      }
      : {},
    isBooked || isReplacement
      ? {
        key: '1',
        primaryTitle: strings.mobile,
        primaryValue: mobile,
      }
      : {},
  ];

  const controlledType = () => {
    if (isRequested && isBookingStateNone) {
      return null;
    }
    return type;
  };

  return (
    <ServiceCard
      customContainerStyle={styles.root}
      eventType={controlledType() as keyof typeof controlledType}
      content={(
        <PharmacistCardContent
          successAction={successAction}
          cardContent={cardContent}
          screenType={screenType}
          imageSuccessAction={imageSuccessAction}
          goBack={goBack}
          isExternalPharmacist={isExternalPharmacist}
          showRating={showRating}
        />
      )}
      footer={
        (isPharmacistProfile || isReplacement) && (
          <PharmacistCardFooter
            customFooterEnd={customFooterEnd}
            successAction={successAction}
            footerContent={
              isSelfService ? selfFooterConfig : pharmacistFooterConfig
            }
          />
        )
      }
    />
  );
};

export default memo(PharmacistCard);
